/**
 * Done. / ChiroCPD — useExportFields Hook
 *
 * Fetches /api/export-fields with 30s TTL cache.
 * Detects stub data mode (all values are placeholder content).
 * Exposes retry function for error states.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '@/lib/api';
import { cacheGet, cacheSet } from '@/lib/cache';
import type { ExportFieldData } from '@/lib/types';
import { EXPORT_CACHE_TTL_MS } from '@/lib/types';

const CACHE_KEY = 'export-fields';

interface ExportFieldsState {
  fields: ExportFieldData[];
  /** True when API returns stub/placeholder data (Day 3 staging) */
  stubData: boolean;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

// The worker returns { fieldDefs, activities, version }
interface RawFieldDef {
  fieldName: string;
  fieldLabel: string;
  fieldOrder: number;
  fieldType: string;
}

interface RawActivityExport {
  id: string;
  title: string;
  activityDate: string;
  activityTypeName: string;
  hours: number;
  hasReflection: boolean;
  fields: {
    fieldName: string;
    fieldLabel: string;
    fieldOrder: number;
    fieldType: string;
    value: string;
  }[];
}

interface ExportResponse {
  fieldDefs: RawFieldDef[];
  activities: RawActivityExport[];
  version: string | null;
}

function deriveStatus(value: string): ExportFieldData['status'] {
  if (!value || value.trim() === '') return 'empty';
  // Partial: value exists but contains an inline guidance note from the server
  if (value.includes('→') || value.includes('missing')) return 'partial';
  return 'complete';
}

export function useExportFields(): ExportFieldsState {
  const [fields, setFields] = useState<ExportFieldData[]>([]);
  const [stubData, setStubData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const retry = useCallback((): void => {
    setFetchKey(k => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchFields(): Promise<void> {
      setLoading(true);
      setError(null);

      // Check cache first
      const cached = cacheGet(CACHE_KEY) as ExportFieldData[] | undefined;
      if (cached) {
        if (!cancelled) {
          setFields(cached);
          setLoading(false);
        }
        return;
      }

      try {
        const raw = await api.get<ExportResponse>('/api/export-fields');

        if (cancelled) return;

        // Aggregate: for each field def, concatenate values across all activities
        const enriched: ExportFieldData[] = raw.fieldDefs
          .sort((a, b) => a.fieldOrder - b.fieldOrder)
          .map(def => {
            // Collect this field's value from every activity, joined by newlines
            const values = raw.activities
              .map(act => {
                const field = act.fields.find(f => f.fieldName === def.fieldName);
                return field?.value ?? '';
              })
              .filter(v => v.trim() !== '');

            const combinedValue = values.join('\n\n');
            return {
              fieldName: def.fieldName,
              fieldLabel: def.fieldLabel,
              fieldOrder: def.fieldOrder,
              fieldType: def.fieldType,
              value: combinedValue,
              aiGenerated: false,
              status: deriveStatus(combinedValue),
            };
          });

        // Stub mode: all fields have empty values
        const allEmpty = enriched.every(f => f.status === 'empty');
        setStubData(allEmpty);

        cacheSet(CACHE_KEY, enriched, EXPORT_CACHE_TTL_MS);
        setFields(enriched);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return; // api.ts redirects
        setError(err instanceof Error ? err.message : 'Failed to load export fields');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchFields();
    return () => { cancelled = true; };
  }, [fetchKey]);

  return { fields, stubData, loading, error, retry };
}
